import React, { useEffect, useState } from 'react';
import Modal2 from 'components/commons/modal/Modal2';
import {
  AdvertisementProps,
  AdvertisementRead,
  getAdvertisementReadStorage,
  setAdvertisementReadStorage,
  useAdvertisement,
} from 'hooks/useAdvertisement';
import { useModal } from 'hooks/useModal';
import { formatDate } from 'utils/formatter';
import { convertFromString } from 'utils/date';

function createContent(
  content: string | undefined
): {
  __html: string;
} {
  return { __html: unescape(content ?? '') };
}

const ShowAdvertisementModal = ({
  advertisements,
}: {
  advertisements: AdvertisementProps[];
}) => {
  const initialIsNotShowAgain = false;
  const [isNotShowAgain, setIsNotShowAgain] = useState<boolean>(
    initialIsNotShowAgain
  );

  const [isShow, { hideModal }] = useModal(true);
  const [modalIndex, setModalIndex] = useState<number>(0);
  const maxIndex = advertisements.length - 1;

  // memo: モーダルに表示する販促情報
  const [displayAdvertisement, setDisplayAdvertisement] = useState<
    AdvertisementProps
  >(advertisements[0]);

  const hide = async () => {
    const advertisementsStorage = getAdvertisementReadStorage();
    if (advertisementsStorage === null) return;

    const updateAdvertisementsStorage = advertisementsStorage.filter(
      advertisementStorage =>
        advertisementStorage.id !== displayAdvertisement.id
    );

    await updateAdvertisementsStorage.push({
      id: displayAdvertisement.id,
      readDay: formatDate(new Date(), 'yyyyMMdd'),
      hidden: isNotShowAgain,
    } as AdvertisementRead);
    setAdvertisementReadStorage(updateAdvertisementsStorage);

    if (modalIndex === maxIndex) {
      hideModal();
    } else {
      setModalIndex(prev => prev + 1);
    }
  };

  useEffect(() => {
    setDisplayAdvertisement(advertisements[modalIndex]);
    setIsNotShowAgain(initialIsNotShowAgain);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIndex]);

  return (
    <Modal2 isShow={isShow} modalBaseClass={'lg:w-400p'}>
      <div className="max-h-modal overflow-y-auto text-bluegray-700">
        <div className="w-full h-full">
          <img
            src={`${displayAdvertisement.imageUrl}`}
            alt=""
            className="w-full h-full"
            style={{ objectFit: 'cover' }}
          />
        </div>
        <div className="flex flex-col pt-4 px-4 break-all">
          <div className="mx-auto py-2 px-6 text-xs font-bold text-white bg-bluegray-700 rounded-full">
            {displayAdvertisement.category}
          </div>
          <div
            className="mt-2 font-bold"
            dangerouslySetInnerHTML={createContent(displayAdvertisement.title)}
          />
          <div
            className="my-2 text-sm whitespace-pre-wrap"
            dangerouslySetInnerHTML={createContent(
              displayAdvertisement.content
            )}
          />
        </div>
        <div
          className="px-4 pt-2 pb-4 sticky bg-white shadow-lg"
          id="accountAddBtnTest"
          style={{ bottom: '0%', left: '0%' }}>
          <a
            href={`${displayAdvertisement.contentUrl}`}
            target="_blank"
            rel="noopener noreferrer">
            <div className="flex flex-row items-center font-bold text-white justify-center rounded-lg shadow py-2 px-4 bg-blue-700">
              {displayAdvertisement.buttonTitle}
            </div>
          </a>
          <div
            className="mt-5 flex flex-row items-center text-sm text-bluegray-500 justify-center rounded-lg py-2 px-4 cursor-pointer"
            onClick={hide}>
            閉じる
          </div>
          <div
            className="mt-2 flex flex-row items-center text-xs text-bluegray-500 justify-center rounded-lg py-2 px-4 cursor-pointer"
            onClick={() => setIsNotShowAgain(prev => !prev)}>
            <div
              className={`icon-check text-white mr-2 rounded-sm ${
                isNotShowAgain ? 'bg-blue-700' : 'bg-bluegray-300'
              }`}></div>
            今後表示しない
          </div>
        </div>
      </div>
    </Modal2>
  );
};

const AdvertisementModalInternal = ({
  advertisements,
}: {
  advertisements: AdvertisementProps[];
}) => {
  const [storageItems, setStorageItems] = useState<AdvertisementRead[] | null>(
    null
  );

  useEffect(() => {
    const toStorageProps = (
      advertisements: AdvertisementProps[]
    ): AdvertisementRead[] => {
      return advertisements.map(item => {
        return {
          id: item.id,
          readDay: null,
          hidden: false,
        } as AdvertisementRead;
      });
    };

    const initialStorageInfo = getAdvertisementReadStorage();
    if (initialStorageInfo === null) {
      const list = toStorageProps(advertisements);
      setAdvertisementReadStorage(list);
    } else {
      const storageIdList = initialStorageInfo.map(item => {
        return item.id;
      });

      const updateAdvertisement = advertisements.filter(
        advertisement => !storageIdList.includes(advertisement.id)
      );
      const updateStorageInfo = toStorageProps(updateAdvertisement);
      setAdvertisementReadStorage(initialStorageInfo.concat(updateStorageInfo));
    }

    const updatedStorageInfo = getAdvertisementReadStorage();
    setStorageItems(updatedStorageInfo);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (storageItems === null) return <></>;

  const today = new Date();
  const oneWeekAgo = new Date(today.setDate(today.getDate() - 7));

  const displaystorageItems = storageItems.filter(
    item =>
      !item.hidden &&
      (item.readDay === null ||
        convertFromString(item.readDay, false).getTime() < oneWeekAgo.getTime())
  );

  const displayAdvertisements = displaystorageItems
    .map(item => {
      return advertisements.find(advertisement => advertisement.id === item.id);
    })
    .filter(v => v) as AdvertisementProps[];

  if (displayAdvertisements.length === 0) return <></>;

  return (
    <ShowAdvertisementModal
      {...{
        advertisements: displayAdvertisements,
      }}
    />
  );
};

export const AdvertisementModal = () => {
  const { advertisements } = useAdvertisement();

  if (advertisements === undefined) return <></>;

  return <AdvertisementModalInternal {...{ advertisements }} />;
};
